import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import type { ListingResponse } from "./types";
import { formatPrice, formatArea } from "../../utils/format";

interface Props {
  listing: ListingResponse;
  onRegisterAnother: () => void;
}

export function BlindPreview({ listing, onRegisterAnother }: Props) {
  const navigate = useNavigate();

  // 미니맵 초기화
  useEffect(() => {
    function initMiniMap() {
      const kakao = window.kakao;
      const container = document.getElementById("blind-mini-map");
      if (!container || !kakao?.maps) return;

      const center = new kakao.maps.LatLng(
        listing.blind_lat,
        listing.blind_lng,
      );
      const map = new kakao.maps.Map(container, { center, level: 7 });

      // 블라인드 핀 (읍면동 중심)
      new kakao.maps.Marker({ position: center, map });

      // 드래그/줌 비활성화
      map.setDraggable(false);
      map.setZoomable(false);
    }

    if (window.kakao?.maps?.LatLng) {
      initMiniMap();
    } else {
      const waitId = setInterval(() => {
        if (window.kakao?.maps?.LatLng) {
          clearInterval(waitId);
          initMiniMap();
        }
      }, 300);
      return () => clearInterval(waitId);
    }
  }, [listing.blind_lat, listing.blind_lng]);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* 헤더 */}
        <div style={styles.successHeader}>
          <span style={styles.checkIcon}>{"✓"}</span>
          <h2 style={styles.successTitle}>블라인드 매물 등록 완료</h2>
          <p style={styles.successSub}>
            다른 중개사에게 아래와 같이 보입니다
          </p>
        </div>

        {/* 매물 카드 미리보기 */}
        <div style={styles.previewCard}>
          <div style={styles.previewTop}>
            <span style={styles.typeBadge}>{listing.property_type}</span>
            <span style={styles.regionText}>
              {listing.region_sido} {listing.region_sigungu}{" "}
              {listing.region_eupmyeondong}
            </span>
          </div>

          <div style={styles.previewInfo}>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>면적</span>
              <span style={styles.infoValue}>
                {formatArea(listing.area_pyeong)}
              </span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>가격</span>
              <span style={styles.priceValue}>
                {formatPrice(listing.price_manwon)}원
              </span>
            </div>
            {listing.zoning && (
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>용도지역</span>
                <span style={styles.infoValue}>{listing.zoning}</span>
              </div>
            )}
            {listing.road_access && (
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>도로</span>
                <span style={styles.infoValue}>{listing.road_access}</span>
              </div>
            )}
          </div>

          {/* 블라인드 미니맵 */}
          <div
            id="blind-mini-map"
            style={styles.miniMap}
          />

          {listing.description && (
            <div style={styles.memoBox}>
              <p style={styles.memoLabel}>공개 메모</p>
              <p style={styles.memoText}>{listing.description}</p>
            </div>
          )}

          {/* 블라인드 안내 */}
          <div style={styles.blindNotice}>
            <span style={styles.lockIcon}>{"🔒"}</span>
            <p style={styles.blindText}>
              정확한 주소와 소유주 정보는 NDA 체결 전까지 공개되지 않습니다
            </p>
          </div>
        </div>

        {/* 액션 버튼 */}
        <div style={styles.actions}>
          <button
            onClick={() => navigate("/dashboard")}
            style={styles.secondaryBtn}
          >
            대시보드로
          </button>
          <button onClick={onRegisterAnother} style={styles.primaryBtn}>
            하나 더 등록
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: "24px 16px",
  },
  card: {
    maxWidth: 540,
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  successHeader: {
    textAlign: "center",
    padding: "16px 0",
  },
  checkIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 48,
    height: 48,
    borderRadius: "50%",
    background: "rgba(16, 185, 129, 0.15)",
    color: "var(--lt-green)",
    fontSize: 24,
    fontWeight: 700,
    marginBottom: 12,
  },
  successTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: "var(--text-primary)",
    margin: "8px 0 4px",
  },
  successSub: {
    fontSize: 14,
    color: "var(--text-muted)",
    margin: 0,
  },
  previewCard: {
    background: "var(--bg-card)",
    border: "1px solid var(--border-light)",
    borderRadius: "var(--radius-xl)",
    padding: 20,
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  previewTop: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  typeBadge: {
    padding: "4px 10px",
    borderRadius: "var(--radius-sm)",
    background: "rgba(212, 168, 83, 0.15)",
    color: "var(--lt-gold)",
    fontSize: 13,
    fontWeight: 600,
  },
  regionText: {
    fontSize: 15,
    fontWeight: 600,
    color: "var(--text-primary)",
  },
  previewInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  infoLabel: {
    fontSize: 13,
    color: "var(--text-muted)",
  },
  infoValue: {
    fontSize: 14,
    fontWeight: 500,
    color: "var(--text-primary)",
  },
  priceValue: {
    fontSize: 16,
    fontWeight: 700,
    color: "var(--lt-gold)",
  },
  miniMap: {
    width: "100%",
    height: 200,
    borderRadius: "var(--radius-lg)",
    border: "1px solid var(--border-light)",
    background: "var(--bg-secondary)",
  },
  memoBox: {
    padding: 12,
    borderRadius: "var(--radius-md)",
    background: "var(--bg-secondary)",
  },
  memoLabel: {
    fontSize: 12,
    color: "var(--text-muted)",
    margin: "0 0 4px",
  },
  memoText: {
    fontSize: 14,
    color: "var(--text-primary)",
    margin: 0,
    lineHeight: 1.5,
  },
  blindNotice: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    borderRadius: "var(--radius-md)",
    background: "rgba(245, 158, 11, 0.1)",
  },
  lockIcon: {
    fontSize: 16,
    flexShrink: 0,
  },
  blindText: {
    fontSize: 13,
    color: "var(--lt-orange)",
    margin: 0,
    lineHeight: 1.4,
  },
  actions: {
    display: "flex",
    gap: 12,
  },
  secondaryBtn: {
    flex: 1,
    padding: "14px",
    borderRadius: "var(--radius-md)",
    border: "1px solid var(--border-medium)",
    background: "transparent",
    color: "var(--text-secondary)",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  primaryBtn: {
    flex: 1,
    padding: "14px",
    borderRadius: "var(--radius-md)",
    border: "none",
    background: "var(--lt-gold)",
    color: "#0F172A",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
};
